import React, { useState, useEffect } from 'react';
import { passwordRegex } from '../../config/regex';
import { API_ROUTES } from '../../config/api-routes';
import { validationMessages } from '../../config/validationMessages';
import './register.css';
import '../AuthModal/auth-modal.css';
import { Button, Card, CardBody, CloseButton } from 'react-bootstrap';
import axiosInstance from '../../api/api';
import { getUserFacingError } from '../../utils/userFacingError';
import OAuthButtons from '../OAuthButtons';
import TurnstileField from '../TurnstileField';
import { useTurnstileWidget } from '../../hooks/useTurnstileWidget';
import AuthLegalNotice from '../Legal/AuthLegalNotice';

const {
  PASSWORDS_DO_NOT_MATCH,
  USERNAME_PASSWORD_CANT_BE_EMPTY,
  REGISTRATION_SUCCESSFUL,
  REGISTRATION_FAILED,
  ERROR_DURING_REGISTRATION,
  PASSWORD_MUST_CONTAIN_8_CHARACTERS,
  ERROR_DURING_RESEND_EMAIL,
} = validationMessages;

interface RegisterProps {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
}

const Register: React.FC<RegisterProps> = ({ setShowModal, onClose }) => {
  const closeModal = () => {
    setShowModal(false);
    onClose?.();
  };
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    linkedin: '',
    fullName: '',
    company: '',
    usertype: '',
  });
  const [message, setMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<boolean>(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>('');
  const [canResend, setCanResend] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(60);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    enforced: turnstileEnforced,
    token: turnstileToken,
    error: captchaError,
    setError: setCaptchaError,
    retry: retryCaptcha,
    resetWidget: resetCaptcha,
  } = useTurnstileWidget('candidate-turnstile-container');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isRecruiter, _] = useState<boolean>(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRegister = async (event: any) => {
    event.preventDefault();
    const { email, password, confirmPassword } = formData;

    if (!email || !password) {
      setMessage(`${USERNAME_PASSWORD_CANT_BE_EMPTY}`);
      return;
    }

    if (password !== confirmPassword) {
      setMessage(`${PASSWORDS_DO_NOT_MATCH}`);
      return;
    }

    if (!passwordRegex.test(password)) {
      setPasswordErrorMessage(`${PASSWORD_MUST_CONTAIN_8_CHARACTERS}`);
      return;
    }

    if (turnstileEnforced && !turnstileToken) {
      setCaptchaError('Please complete the CAPTCHA challenge.');
      return;
    }

    try {
      setIsSubmitting(true);
      setCaptchaError('');
      if (isRecruiter) {
        // Recruiter Registration: Send Verification Code
        const verifyResponse = await axiosInstance.post(API_ROUTES.AUTH.SEND_VERIFICATION, {
          email,
          usertype: 'recruiter',
        });

        if (verifyResponse.status === 200) {
          setMessage('Verification code sent! Check your email.');
        } else {
          setMessage('Failed to send verification code. Please try again.');
          return;
        }
      }

      // Register the user (both candidates & recruiters)
      const response = await axiosInstance.post(
        API_ROUTES.AUTH.REGISTER,
        {
          email,
          password,
          usertype: isRecruiter ? 'recruiter' : 'candidate', // Assign role dynamically
          ...(turnstileEnforced ? { turnstileToken } : {}),
        },
        { suppressErrorToast: true }
      );

      const data = response.data;

      if (data.success) {
        setMessage('');
        setSuccessMessage(true);
        setCanResend(false);
        setResendTimer(60);
      } else {
        setMessage(`${REGISTRATION_FAILED} ${data.message}`);
      }
    } catch (error: unknown) {
      resetCaptcha();
      setMessage(getUserFacingError(error, ERROR_DURING_REGISTRATION));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    try {
      const response = await axiosInstance.post(
        API_ROUTES.AUTH.RESEND_EMAIL,
        {
          email: formData.email,
          type: 'registration',
        },
        { suppressErrorToast: true }
      );

      if (response.data.success) {
        setCanResend(false);
        setResendTimer(60);
      } else {
        setMessage(`Failed to resend verification email: ${response.data.message}`);
      }
    } catch (error: unknown) {
      setMessage(getUserFacingError(error, ERROR_DURING_RESEND_EMAIL));
    }
  };

  return (
    <div className="auth-modal">
      {successMessage ? (
        <Card className="auth-modal-card">
          <Card.Header className="auth-modal-header">
            <Card.Title className="auth-modal-title">{REGISTRATION_SUCCESSFUL}</Card.Title>
            <CloseButton onClick={closeModal} />
          </Card.Header>
          <CardBody className="auth-modal-body">
            {message && (
              <div className="alert alert-danger" role="alert">
                {message}
              </div>
            )}
            <Card.Text className="auth-support-text">
              Thank you for registering with us. To complete your registration, please verify your
              email address.
              <br />
              <br />
              We have sent a verification email to your inbox. Please check your email and follow
              the instructions to activate your account.
              <br />
              <br />
              If you do not see the email, please check your spam or junk folder.
              <br />
              <br />
              <strong>
                Please make sure you have received the verification email before closing this modal.
                Closing this modal will disable the "Resend Verification Email" button.
              </strong>
              <br />
              <br />
              {canResend ? (
                <Button
                  variant="success"
                  onClick={handleResendEmail}
                  className="submit-btn-sign-up"
                >
                  Resend Verification Email
                </Button>
              ) : (
                <Button variant="success" className="submit-btn-sign-up" disabled>
                  Resend available in {resendTimer}s
                </Button>
              )}
            </Card.Text>
          </CardBody>
        </Card>
      ) : (
        <Card className="auth-modal-card">
          <Card.Header className="auth-modal-header">
            <h3 className="auth-modal-title">Create your account</h3>
            <CloseButton onClick={closeModal} />
          </Card.Header>
          <CardBody className="auth-modal-body">
            {message && (
              <div className="alert alert-danger" role="alert">
                {message}
              </div>
            )}
            <form className="auth-form" onSubmit={handleRegister}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email*
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password*
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                {passwordErrorMessage !== '' && (
                  <div className="alert alert-danger" role="alert">
                    {passwordErrorMessage}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password<span>*</span>
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {isRecruiter && (
                <>
                  <div className="mb-3">
                    <label htmlFor="fullName" className="form-label">
                      Full Name*
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="company" className="form-label">
                      Company*
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="company"
                      name="company"
                      value={formData.company || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="linkedin" className="form-label">
                      LinkedIn Profile URL*
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="linkedin"
                      name="linkedin"
                      value={formData.linkedin || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </>
              )}
              <div className="auth-actions">
                {turnstileEnforced && (
                  <TurnstileField
                    containerId="candidate-turnstile-container"
                    error={captchaError}
                    onRetry={retryCaptcha}
                  />
                )}
                <button
                  type="submit"
                  className="submit-btn-sign-up"
                  disabled={isSubmitting || (turnstileEnforced && !turnstileToken)}
                >
                  {isSubmitting ? 'Submitting...' : 'Register'}
                </button>
              </div>

              <OAuthButtons mode="register" />
              <AuthLegalNotice action="registering" />
            </form>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default Register;
